// Ad parameters on links out of the site (home-page ad test, Veronica, 2026-10-03).
//
// Meta ads now land on the home page instead of /start and /ios. A visitor from a Meta ad must
// keep their ad parameters on every link to app.workspace369.com, for two reasons:
// - the app's phone gate (Workspace369 web/index.html) sends phones to the App Store unless the
//   URL carries a campaign parameter, so without them an iPhone visitor from an ad never reaches
//   web sign-up;
// - the parameters in the URL back up w369_attr / _fbp / _fbc, which Safari sometimes deletes.
// The App Store button gets the same `ct`/`pt`/`mt` link /ios builds.
//
// Pure: callers pass the page's query string, the w369_attr `last_meta` touch and the consent
// decision. Unit tested in tests/tracking/adLinks.test.ts. Wired up in plugins/ad-links.client.ts
// and components/TrialSignupDialog.vue.
/** The ad parameters carried onto links to the app, in this order. */
export const AD_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'w369_src',
  'w369_plc',
  'fbclid',
] as const;

export type AdParams = Partial<Record<(typeof AD_PARAM_KEYS)[number], string>>;
type TouchLike = Partial<Record<string, unknown>> | null | undefined;

const APP_HOST = 'app.workspace369.com';
const APP_STORE_HOST = 'apps.apple.com';
const APP_STORE_LISTING = /\/id6475013419\/?$/;
/** Same bound the redirect pages use; a longer value is corrupt, so it is dropped whole. */
const MAX_VALUE_LENGTH = 500;

const clean = (value: unknown): string =>
  typeof value === 'string' && value !== '' && value.length <= MAX_VALUE_LENGTH ? value : '';

const pick = (get: (key: string) => unknown): AdParams => {
  const out: AdParams = {};
  for (const key of AD_PARAM_KEYS) {
    const value = clean(get(key));
    if (value) out[key] = value;
  }
  return out;
};

/**
 * This visitor's Meta ad parameters: from the current page URL when it is a Meta ad landing
 * (`utm_source=meta`), otherwise from the `last_meta` touch in w369_attr. null for anyone not
 * from a Meta ad, and for everyone without granted ad consent — the same rule w369_attr uses.
 */
export function metaAdParams(search: string, lastMeta: TouchLike, adConsentGranted: boolean): AdParams | null {
  if (!adConsentGranted) return null;
  const query = new URLSearchParams(search.replace(/^\?/, ''));
  if (query.get('utm_source') === 'meta') return pick((key) => query.get(key));
  if (lastMeta && lastMeta.utm_source === 'meta') return pick((key) => lastMeta[key]);
  return null;
}

/** Current campaign, then the last consented campaign touch. Never combine channels.
 * Meta-only click ids remain restricted to Meta; other sources forward UTM labels only.
 * Keep the same granted-consent requirement as the existing cookie and Meta links.
 */
export function campaignParams(
  search: string,
  last: TouchLike,
  lastMeta: TouchLike,
  consentGranted: boolean,
): AdParams | null {
  if (!consentGranted) return null;
  const query = new URLSearchParams(search.replace(/^\?/, ''));
  const current = pick(key => query.get(key));
  const previous = pick(key => last?.[key]);
  const hasCampaign = (params: AdParams) => AD_PARAM_KEYS.some(key => key.startsWith('utm_') && params[key]);
  const selected = hasCampaign(current) ? current : hasCampaign(previous) ? previous : metaAdParams('', lastMeta, true);
  if (!selected) return null;
  if (selected.utm_source !== 'meta') {
    delete selected.fbclid;
    delete selected.w369_src;
    delete selected.w369_plc;
  }
  return selected;
}

const parse = (href: string): URL | null => {
  try {
    return new URL(href);
  } catch {
    return null;
  }
};

/** True for a link into the web app (https://app.workspace369.com/…). */
export function isAppLink(href: string): boolean {
  const url = parse(href);
  return !!url && url.protocol === 'https:' && url.hostname === APP_HOST;
}

/** True for a link to Workspace369's App Store listing (any storefront path). */
export function isAppStoreListing(href: string): boolean {
  const url = parse(href);
  return !!url && url.hostname === APP_STORE_HOST && APP_STORE_LISTING.test(url.pathname);
}

/** `href` with the ad parameters added. Existing parameters (`plan`, `ref`, …) are kept as they are. */
export function withAdParams(href: string, params: AdParams | null): string {
  if (!params) return href;
  const url = parse(href);
  if (!url) return href;
  for (const key of AD_PARAM_KEYS) {
    const value = params[key];
    if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
  }
  return url.toString();
}

/** A dialog may stay open while consent is revoked. Drop copied campaign tags
 * at submission time; keep functional signup parameters such as plan/ref/lead_id.
 */
export function signupCampaignUrl(href: string, params: AdParams | null, consentGranted: boolean): string {
  if (consentGranted) return withAdParams(href, params);
  const url = parse(href);
  if (!url) return href;
  for (const key of AD_PARAM_KEYS) url.searchParams.delete(key);
  return url.toString();
}

/**
 * The query to hand `appStoreUrl` (redirectDestination.ts) for a visitor from a Meta ad, so the
 * App Store link is built exactly like /ios: `ct=w369_meta_<utm_content>` (Apple's characters,
 * 30 max), `pt` and `mt=8`.
 */
export function appStoreSearchForAd(params: AdParams): string {
  return params.utm_content ? `?utm_content=${encodeURIComponent(params.utm_content)}` : '';
}
